"use client";

import { useEffect, useState } from "react";

// Read CSS custom properties once and store them
const getBreakpoints = () => {
  const lgBreakpoint = getComputedStyle(document.documentElement).getPropertyValue("--breakpoint-lg").trim();
  const mdBreakpoint = getComputedStyle(document.documentElement).getPropertyValue("--breakpoint-md").trim();

  // Validate breakpoints exist and are valid
  if (!lgBreakpoint || !mdBreakpoint) {
    // Fallback to reasonable defaults that match Tailwind's lg and md breakpoints
    return {
      lgBreakpoint: "1280px",
      mdBreakpoint: "768px",
      lgValue: 1280,
      mdValue: 768,
    };
  }

  // Convert to numbers for calculations (remove 'px' suffix)
  const lgValue = parseInt(lgBreakpoint.replace("px", ""));
  const mdValue = parseInt(mdBreakpoint.replace("px", ""));

  // Validate parsed values
  if (isNaN(lgValue) || isNaN(mdValue)) {
    // Fallback to reasonable defaults
    return {
      lgBreakpoint: "1280px",
      mdBreakpoint: "768px",
      lgValue: 1280,
      mdValue: 768,
    };
  }

  return {
    lgBreakpoint,
    mdBreakpoint,
    lgValue,
    mdValue,
  };
};

// Calculate initial device state - always return same value on server and client
// to prevent hydration mismatches. The actual device state will be set in useEffect.
const getInitialDeviceState = () => {
  // Always return the same initial state to ensure server and client render match
  return { isDesktop: false, isTablet: false, isMobile: false };
};

export const useDevice = () => {
  // Use consistent initial state for both server and client to prevent hydration errors
  const initialState = getInitialDeviceState();

  const [isDesktop, setIsDesktop] = useState(initialState.isDesktop);
  const [isTablet, setIsTablet] = useState(initialState.isTablet);
  const [isMobile, setIsMobile] = useState(initialState.isMobile);
  // Track when device detection is complete (after hydration)
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    const breakpoints = getBreakpoints();

    const checkDevice = () => {
      const isDesktopQuery = window.matchMedia(`(min-width: ${breakpoints.lgBreakpoint})`).matches;
      const isTabletQuery = window.matchMedia(
        `(min-width: ${breakpoints.mdBreakpoint}) and (max-width: ${breakpoints.lgValue - 1}px)`,
      ).matches;
      const isMobileQuery = window.matchMedia(`(max-width: ${breakpoints.mdValue - 1}px)`).matches;

      setIsDesktop(isDesktopQuery);
      setIsTablet(isTabletQuery);
      setIsMobile(isMobileQuery);
      // Mark as mounted after first device check
      setIsMounted(true);
    };

    // Initial check
    checkDevice();

    // Set up media query listeners using the stored breakpoint values
    const desktopQuery = window.matchMedia(`(min-width: ${breakpoints.lgBreakpoint})`);
    const tabletQuery = window.matchMedia(
      `(min-width: ${breakpoints.mdBreakpoint}) and (max-width: ${breakpoints.lgValue - 1}px)`,
    );
    const mobileQuery = window.matchMedia(`(max-width: ${breakpoints.mdValue - 1}px)`);

    const handleDesktopChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    const handleTabletChange = (e: MediaQueryListEvent) => setIsTablet(e.matches);
    const handleMobileChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);

    desktopQuery.addEventListener("change", handleDesktopChange);
    tabletQuery.addEventListener("change", handleTabletChange);
    mobileQuery.addEventListener("change", handleMobileChange);

    return () => {
      desktopQuery.removeEventListener("change", handleDesktopChange);
      tabletQuery.removeEventListener("change", handleTabletChange);
      mobileQuery.removeEventListener("change", handleMobileChange);
    };
  }, []);

  return {
    isDesktop,
    isTablet,
    isMobile,
    isMounted,
  };
};
